/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { ReactNode } from 'react';

import { cssMap as unboundedCssMap } from '@compiled/react';

import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

export interface PanelSubheaderProps {
	/**
	 * The title text to display in the subheader.
	 */
	title: string;
	/**
	 * An optional cover image to display above the subheader title.
	 * Accepts any React node — typically an `<img>` element, a media component,
	 * or a custom image wrapper.
	 *
	 * The image fills the full width of the panel and is cropped to a fixed height of 160px.
	 *
	 * Accessibility: any `<img>` passed here must include a meaningful `alt` attribute describing the
	 * image, or `alt=""` if it is purely decorative, to satisfy WCAG 1.1.1 (Non-text Content). This
	 * is not enforced at the type level, so consumers are responsible for providing it.
	 */
	coverImage?: ReactNode;
	/**
	 * An optional breadcrumbs element to display directly above the title.
	 * Designed for the `@atlaskit/breadcrumbs` component (small variant), which is 24px tall.
	 * The slot reserves a 24px-tall row and applies the correct gap between the breadcrumbs and title.
	 */
	breadcrumbs?: ReactNode;
	/**
	 * An optional icon to display at the start of the title.
	 * Accepts any React node — typically an `@atlaskit/icon` component.
	 */
	titleIcon?: ReactNode;
	/**
	 * An optional inline edit control to render in place of the static title, allowing the title to
	 * be edited in place. Typically a `SubheaderInlineEdit`, which is pre-styled to match the title.
	 *
	 * When provided, this is rendered instead of the `title` text. The `title` prop should still be
	 * supplied as the current value passed to the inline edit control.
	 */
	inlineEdit?: ReactNode;
	/**
	 * A unique string that appears as data attribute `data-testid` in the rendered code, serving as a hook for automated tests.
	 */
	testId?: string;
}

const styles = cssMap({
	subheader: {
		paddingBlockEnd: token('space.200'),
		paddingInlineStart: token('space.300'),
		paddingInlineEnd: token('space.300'),
	},
	breadcrumbsSlot: {
		display: 'flex',
		alignItems: 'center',
		minHeight: '24px',
	},
});

const unboundedStyles = unboundedCssMap({
	// Center the leading title element in a 24x24 slot, cap a larger element to that slot so it does
	// not overflow or push the title, and keep the slot from shrinking next to a long title. The cap
	// uses max-width/height (not fixed) so a smaller icon stays its natural size and is centered,
	// rather than being stretched to 24px and pinned to the top-left.
	titleIconConstrained: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '1.5rem',
		height: '1.5rem',
		flexShrink: 0,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles
		'& > *': {
			maxWidth: '1.5rem',
			maxHeight: '1.5rem',
		},
	},
	titleIconInlineEditOffset: {
		// Align the icon to the first editable title line. Inline edit's read view has extra vertical
		// padding, and multiline titles should not center the icon against the full text block.
		marginBlockStart: token('space.075'),
	},
	coverImageContainer: {
		marginInlineStart: token('space.negative.300'),
		marginInlineEnd: token('space.negative.300'),
		height: '160px',
		overflow: 'hidden',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles
		'& > *': {
			display: 'block',
			width: '100%',
			height: '100%',
			objectFit: 'cover',
		},
	},
});

/**
 * The PanelSubheader component provides a slot for subheaders with a title,
 * and an optional cover image that fills the panel width above the title.
 */
export function PanelSubheader({
	title,
	titleIcon,
	coverImage,
	breadcrumbs,
	inlineEdit,
	testId,
}: PanelSubheaderProps): JSX.Element {
	return (
		<Flex testId={testId} direction="column" gap="space.150" xcss={styles.subheader}>
			{coverImage && <div css={unboundedStyles.coverImageContainer}>{coverImage}</div>}
			<Flex direction="column" gap="space.100">
				{breadcrumbs && <Box xcss={styles.breadcrumbsSlot}>{breadcrumbs}</Box>}
				<Flex alignItems="start" gap="space.150">
					{titleIcon && (
						<div
							css={[
								unboundedStyles.titleIconConstrained,
								inlineEdit && unboundedStyles.titleIconInlineEditOffset,
							]}
						>
							{titleIcon}
						</div>
					)}
					{inlineEdit ?? (
						<Heading as="h3" size="medium">
							{title}
						</Heading>
					)}
				</Flex>
			</Flex>
		</Flex>
	);
}
